/**
 * Base API configuration and utilities
 */
import axios from 'axios';
import { acquireBackendToken, withAuthHeader } from '../../utils/authApi';

const baseURL = process.env.REACT_APP_BACKEND_BASEURL;

let tokenProvider = null;
let msalContext = null;

export function setApiTokenProvider(provider) {
  tokenProvider = provider;
}

export function setMsalContext(instance, account) {
  msalContext = instance && account ? { instance, account } : null;
}

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: `${baseURL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  async (config) => {
    if (tokenProvider) {
      const accessToken = await tokenProvider();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: refresh token and retry once on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === 401 && originalRequest && !originalRequest._authRetry && msalContext) {
      originalRequest._authRetry = true;

      try {
        const tokenResult = await acquireBackendToken(
          msalContext.instance,
          msalContext.account,
          { forceRefresh: true }
        );

        if (tokenResult?.accessToken) {
          originalRequest.headers = withAuthHeader(
            tokenResult.accessToken,
            originalRequest.headers || {}
          );
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('API token refresh failed:', refreshError.message);
      }
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

/**
 * Generic API request wrapper
 */
export const apiRequest = async (method, url, data = null, config = {}) => {
  try {
    const response = await apiClient({
      method,
      url,
      data,
      ...config,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

